import React, { useCallback, useMemo } from 'react';
import {
  Autocomplete,
  Box,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Grid,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import { useSelector } from 'react-redux';
import FilterListIcon from '@mui/icons-material/FilterList';
import {
  StyledFilterCard,
  StyledControlsBox,
  StyledSortMenu,
  StyledButton,
  CompanyFilterToggle,
  CompanyFilterToggleButton,
  ProtectedDomainsToggle,
  ProtectedDomainsToggleButton,
} from './Style/FilterCard.styles';
import { DatePickers, Dropdown } from '../../components';
import {
  useCompaniesQuery,
  useThreatStatusesQuery,
  useServicesQuery,
  useSourcesQuery,
} from '../../queries/lookups';
import { useAllProtectedDomainsQuery } from '../../queries/protectedDomains';
import {
  defaultEndDate,
  defaultStartDate,
  disableAfterToday,
  getDateOrDefault,
  isDisabledStatus,
  buildSourceOptions,
  formatServiceName,
  getSourceColor,
  BRP_VERDICT_OPTIONS,
  BRP_CONFIDENCE_OPTIONS,
} from '../../utils';
import { getDateRangePresetsForPicker } from '../../utils/datePresets';
import { Kbd } from '../../components/ThreatTable/ThreatPreviewPanel';
import { modEnterHint } from '../ThreadsDetails/Cockpit/shortcutUtils';

const FilterCard = ({
  onChangeSatatuses,
  onChangeCompany,
  onChangeDate,
  handleApplyFilter,
  handleResetFilter,
  getPickerStartDate,
  getPickerEndDate,
  openSortBy,
  setOpenSortBy,
  handleUpdatedASCE,
  handleUpdatedDESC,
  handleDetectedASCE,
  handleDetectedDESC,
  handleDomainASCE,
  handleDomainDESC,
  handleCompanyGroup,
  handleBrpVerdictASCE,
  handleBrpVerdictDESC,
  handleBrpConfidenceASCE,
  handleBrpConfidenceDESC,
  handleBrpScoreASCE,
  handleBrpScoreDESC,
  onChangeService,
  getServicesData,
  onChangeSource,
  getSourcesData,
  onChangeBrpVerdict,
  onChangeBrpConfidence,
  getBrpVerdictData,
  getBrpConfidenceData,
  onChangeExcludeCompany,
  isCustomerReviewPage,
  sortConfig,
  companyFilterMode,
  onToggleCompanyMode,
  includeChildCompanies,
  onToggleIncludeChildCompanies,
  isResolvingChildCompanies,
  addedChildCompaniesCount,
  protectedDomainMode,
  onToggleProtectedDomainMode,
  assetFilterMode,
  onToggleAssetMode,
  onChangeAssetsInclude,
  onChangeAssetsExclude,
}: any) => {
  const {
    companyFilterData,
    reviewStatusFilterData,
    statusFilterData,
    companysExclude,
    protectedAssetsInclude = [],
    protectedAssetsExclude = [],
  } = useSelector((state: any) => state.filterList);

  const { data: companyList } = useCompaniesQuery();
  const { data: statusList } = useThreatStatusesQuery();
  const { data: serviceList } = useServicesQuery();
  const { data: sourceList } = useSourcesQuery();

  const handleCloseSortBy = () => {
    setOpenSortBy(null);
  };

  const companiesData = useMemo(
    () =>
      companyList?.map(company => ({
        label: company.Name,
        id: company.Id,
      })) || [],
    [companyList],
  );
  const statusesData = useMemo(() => {
    const statuses = statusList?.map(status => ({
      label: status.name,
      id: status.id,
      disabled: isDisabledStatus(status.name, isCustomerReviewPage),
    }));

    return (
      statuses?.sort(
        (a: { disabled: boolean }, b: { disabled: boolean }) =>
          Number(a.disabled) - Number(b.disabled),
      ) || []
    );
  }, [statusList, isCustomerReviewPage]);

  const servicesData = useMemo(
    () =>
      serviceList?.map(service => ({
        label: service?.name,
        id: service?.id,
      })) || [],
    [serviceList],
  );

  const sourcesData = useCallback(
    () => buildSourceOptions(sourceList),
    [sourceList],
  );

  const handleCompanyModeChange = (
    _event: React.MouseEvent<HTMLElement>,
    newMode: 'include' | 'exclude' | null,
  ) => {
    if (newMode !== null) {
      onToggleCompanyMode(newMode);
    }
  };

  const handleAssetModeChange = (
    _event: React.MouseEvent<HTMLElement>,
    newMode: 'include' | 'exclude' | null,
  ) => {
    if (newMode !== null) {
      onToggleAssetMode(newMode);
    }
  };

  // Asset-name suggestions: only available when exactly one company is in the
  // include filter (there is no global protected-assets endpoint).
  const singleCompanyId =
    companyFilterData.length === 1 ? companyFilterData[0].id : undefined;
  const { data: companyAssets } = useAllProtectedDomainsQuery(
    {
      companyId: singleCompanyId ? String(singleCompanyId) : undefined,
      hideDeactivated: true,
    },
    !!singleCompanyId,
  );
  const assetSuggestions = useMemo(
    () =>
      companyAssets?.items?.map(
        (item: { domain_name: string }) => item.domain_name,
      ) ?? [],
    [companyAssets],
  );

  const handleAssetValuesChange = (
    _event: React.SyntheticEvent,
    value: (string | { label: string })[],
  ) => {
    const names = Array.from(
      new Set(
        value
          .map(v => (typeof v === 'string' ? v : v.label).trim())
          .filter(Boolean),
      ),
    );
    if (assetFilterMode === 'include') {
      onChangeAssetsInclude(names);
    } else {
      onChangeAssetsExclude(names);
    }
  };

  const handleProtectedDomainModeChange = (
    _event: React.MouseEvent<HTMLElement>,
    newMode: 'active_only' | 'all' | null,
  ) => {
    if (newMode !== null) {
      onToggleProtectedDomainMode(newMode);
    }
  };

  return (
    <StyledFilterCard data-testid="filter-card-container">
      <Grid container rowSpacing={1.5} columnSpacing={{ xs: 1, sm: 2, md: 2 }}>
        {/* Row 1: Status | Service | Date Range */}
        <Grid item xs={4} id="status">
          <Dropdown
            dataTestid="status-dropdown"
            items={statusesData || []}
            size="small"
            placeholder="Status"
            label="Status"
            onChange={onChangeSatatuses}
            selectedValues={
              isCustomerReviewPage ? reviewStatusFilterData : statusFilterData
            }
            multiple={true}
          />
        </Grid>
        <Grid item xs={4} id="services">
          <Dropdown
            dataTestid="service-dropdown"
            items={servicesData || []}
            size="small"
            placeholder="Service"
            label="Service"
            onChange={onChangeService}
            selectedValues={getServicesData}
            multiple={true}
            formatLabel={formatServiceName}
          />
        </Grid>
        <Grid item xs={4} id="date-pickers">
          <DatePickers
            size="small"
            onChange={onChangeDate}
            defaultStartDate={getDateOrDefault(
              getPickerStartDate,
              defaultStartDate,
            )}
            defaultEndDate={getDateOrDefault(getPickerEndDate, defaultEndDate)}
            disableAfterToday={disableAfterToday}
            ranges={getDateRangePresetsForPicker()}
          />
        </Grid>

        {/* Row 2: Sources | Company w/ toggle | Controls */}
        <Grid
          item
          xs={4}
          id="sources"
          sx={{
            display: 'flex',
            flexDirection: 'column',
            '& .MuiAutocomplete-root': { flex: 1 },
            '& .MuiFormControl-root': { height: '100%' },
            '& .MuiInputBase-root': {
              height: '100%',
              alignContent: 'flex-start',
            },
          }}
        >
          <Dropdown
            dataTestid="sources-dropdown"
            items={sourcesData() || []}
            size="small"
            placeholder="Sources"
            label="Sources"
            onChange={onChangeSource}
            selectedValues={getSourcesData}
            multiple={true}
            getOptionColor={option => getSourceColor(option.value)}
          />
        </Grid>
        <Grid item xs={4} id="companies">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <Typography
              variant="caption"
              sx={{ fontWeight: 500, color: 'text.secondary' }}
            >
              Company
            </Typography>
            <CompanyFilterToggle
              value={companyFilterMode}
              exclusive
              onChange={handleCompanyModeChange}
              size="small"
            >
              <CompanyFilterToggleButton value="include">
                Include
              </CompanyFilterToggleButton>
              <CompanyFilterToggleButton value="exclude">
                Exclude
              </CompanyFilterToggleButton>
            </CompanyFilterToggle>
          </Box>
          <Dropdown
            dataTestid="company-dropdown"
            items={companiesData}
            size="small"
            placeholder={
              companyFilterMode === 'include'
                ? 'Include companies'
                : 'Exclude companies'
            }
            onChange={
              companyFilterMode === 'include'
                ? onChangeCompany
                : onChangeExcludeCompany
            }
            selectedValues={
              companyFilterMode === 'include'
                ? companyFilterData
                : companysExclude
            }
            multiple={true}
            limitTags={5}
          />
          <FormControlLabel
            sx={{ ml: 0, mt: 0.25 }}
            control={
              <Checkbox
                size="small"
                sx={{ p: 0.5 }}
                checked={!!includeChildCompanies}
                onChange={event =>
                  onToggleIncludeChildCompanies(event.target.checked)
                }
                data-testid="company-include-children-toggle"
              />
            }
            label={
              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.5,
                }}
              >
                Include child companies
                {includeChildCompanies && isResolvingChildCompanies && (
                  <CircularProgress size={10} />
                )}
                {includeChildCompanies &&
                  !isResolvingChildCompanies &&
                  addedChildCompaniesCount > 0 && (
                    <span data-testid="company-children-count">
                      (+{addedChildCompaniesCount})
                    </span>
                  )}
              </Typography>
            }
          />
        </Grid>
        <Grid
          item
          xs={4}
          id="controls"
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-end',
          }}
        >
          <StyledControlsBox sx={{ height: 'auto' }}>
            <StyledButton
              data-testid="sort-button"
              onClick={(event: React.MouseEvent<HTMLElement>) =>
                setOpenSortBy(event.currentTarget)
              }
              label="Sort By"
              variant="contained"
              startIcon={<FilterListIcon />}
            ></StyledButton>
            <StyledSortMenu
              data-testid="sort-menu"
              anchorEl={openSortBy}
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              keepMounted
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              open={Boolean(openSortBy)}
              onClose={handleCloseSortBy}
            >
              <MenuItem
                onClick={handleUpdatedASCE}
                selected={
                  sortConfig?.sortBy === 'updated_at' &&
                  sortConfig?.sortOrder === 'ASCENDING'
                }
              >
                Update date <span> (older first)</span>
              </MenuItem>
              <MenuItem
                onClick={handleUpdatedDESC}
                selected={
                  sortConfig?.sortBy === 'updated_at' &&
                  sortConfig?.sortOrder === 'DESCENDING'
                }
              >
                Update date <span> (newer first)</span>
              </MenuItem>
              <MenuItem
                onClick={handleDetectedASCE}
                selected={
                  sortConfig?.sortBy === 'detected_at' &&
                  sortConfig?.sortOrder === 'ASCENDING'
                }
              >
                Detection date<span> (older first)</span>
              </MenuItem>
              <MenuItem
                onClick={handleDetectedDESC}
                selected={
                  sortConfig?.sortBy === 'detected_at' &&
                  sortConfig?.sortOrder === 'DESCENDING'
                }
              >
                Detection date <span> (newer first)</span>
              </MenuItem>
              <MenuItem
                onClick={handleDomainASCE}
                selected={
                  sortConfig?.sortBy === 'protected_asset_name' &&
                  sortConfig?.sortOrder === 'ASCENDING'
                }
              >
                Target domain <span> (A → Z)</span>
              </MenuItem>
              <MenuItem
                onClick={handleDomainDESC}
                selected={
                  sortConfig?.sortBy === 'protected_asset_name' &&
                  sortConfig?.sortOrder === 'DESCENDING'
                }
              >
                Target domain <span> (Z → A)</span>
              </MenuItem>
              <MenuItem
                onClick={handleCompanyGroup}
                selected={sortConfig?.sortBy === 'company_id'}
              >
                Group by Company
              </MenuItem>
              <MenuItem
                onClick={handleBrpScoreDESC}
                selected={
                  sortConfig?.sortBy === 'brp_score' &&
                  sortConfig?.sortOrder === 'DESCENDING'
                }
              >
                BRP Score <span> (high first)</span>
              </MenuItem>
              <MenuItem
                onClick={handleBrpScoreASCE}
                selected={
                  sortConfig?.sortBy === 'brp_score' &&
                  sortConfig?.sortOrder === 'ASCENDING'
                }
              >
                BRP Score <span> (low first)</span>
              </MenuItem>
              <MenuItem
                onClick={handleBrpVerdictASCE}
                selected={
                  sortConfig?.sortBy === 'brp_verdict' &&
                  sortConfig?.sortOrder === 'ASCENDING'
                }
              >
                BRP Verdict <span> (A → Z)</span>
              </MenuItem>
              <MenuItem
                onClick={handleBrpVerdictDESC}
                selected={
                  sortConfig?.sortBy === 'brp_verdict' &&
                  sortConfig?.sortOrder === 'DESCENDING'
                }
              >
                BRP Verdict <span> (Z → A)</span>
              </MenuItem>
              <MenuItem
                onClick={handleBrpConfidenceDESC}
                selected={
                  sortConfig?.sortBy === 'brp_confidence' &&
                  sortConfig?.sortOrder === 'DESCENDING'
                }
              >
                BRP Confidence <span> (high first)</span>
              </MenuItem>
              <MenuItem
                onClick={handleBrpConfidenceASCE}
                selected={
                  sortConfig?.sortBy === 'brp_confidence' &&
                  sortConfig?.sortOrder === 'ASCENDING'
                }
              >
                BRP Confidence <span> (low first)</span>
              </MenuItem>
            </StyledSortMenu>
            <StyledButton
              data-testid="apply-filter-button"
              variant="contained"
              onClick={handleApplyFilter}
            >
              Apply
              {/* On the teal contained button the default `divider` border
                  reads almost black in dark mode — use a pale teal instead. */}
              <Kbd k={modEnterHint()} sx={{ borderColor: '#baf2ec' }} />
            </StyledButton>
            <StyledButton
              data-testid="reset-filter-button"
              onClick={handleResetFilter}
              variant="outlined"
            >
              Reset
              <Kbd k="⇧R" />
            </StyledButton>
          </StyledControlsBox>
        </Grid>

        {/* Row 3: Protected Assets — include/exclude list + active-only scope */}
        <Grid item xs={4} id="protected-assets">
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.75,
              mb: 0.5,
              flexWrap: 'nowrap',
            }}
          >
            <Typography
              variant="caption"
              noWrap
              sx={{ fontWeight: 500, color: 'text.secondary' }}
            >
              Protected Assets
            </Typography>
            <CompanyFilterToggle
              data-testid="asset-filter-toggle"
              value={assetFilterMode}
              exclusive
              onChange={handleAssetModeChange}
              size="small"
            >
              <CompanyFilterToggleButton
                data-testid="asset-filter-include"
                value="include"
              >
                Include
              </CompanyFilterToggleButton>
              <CompanyFilterToggleButton
                data-testid="asset-filter-exclude"
                value="exclude"
              >
                Exclude
              </CompanyFilterToggleButton>
            </CompanyFilterToggle>
            <Box sx={{ flex: 1 }} />
            <ProtectedDomainsToggle
              data-testid="threats-protected-domains-toggle"
              value={protectedDomainMode}
              exclusive
              onChange={handleProtectedDomainModeChange}
              size="small"
            >
              <ProtectedDomainsToggleButton
                data-testid="threats-protected-domains-active-only"
                value="active_only"
              >
                Active only
              </ProtectedDomainsToggleButton>
              <ProtectedDomainsToggleButton
                data-testid="threats-protected-domains-all"
                value="all"
              >
                All
              </ProtectedDomainsToggleButton>
            </ProtectedDomainsToggle>
          </Box>
          <Autocomplete
            data-testid="asset-dropdown"
            multiple
            freeSolo
            autoSelect
            size="small"
            limitTags={5}
            options={assetSuggestions}
            value={
              assetFilterMode === 'include'
                ? protectedAssetsInclude
                : protectedAssetsExclude
            }
            onChange={handleAssetValuesChange}
            renderInput={params => (
              <TextField
                {...params}
                size="small"
                placeholder={`${
                  assetFilterMode === 'include' ? 'Include' : 'Exclude'
                } asset names${
                  singleCompanyId ? '' : ' (pick 1 company for suggestions)'
                }`}
              />
            )}
          />
        </Grid>
        {/* Row 3 (cont.): BRP classifier filters (ENG-718 / REQ-4) */}
        <Grid item xs={4} id="brp-verdict">
          <Dropdown
            dataTestid="brp-verdict-dropdown"
            items={BRP_VERDICT_OPTIONS}
            size="small"
            placeholder="BRP Verdict"
            label="BRP Verdict"
            onChange={onChangeBrpVerdict}
            selectedValues={getBrpVerdictData}
            multiple={true}
          />
        </Grid>
        <Grid item xs={4} id="brp-confidence">
          <Dropdown
            dataTestid="brp-confidence-dropdown"
            items={BRP_CONFIDENCE_OPTIONS}
            size="small"
            placeholder="BRP Confidence"
            label="BRP Confidence"
            onChange={onChangeBrpConfidence}
            selectedValues={getBrpConfidenceData}
            multiple={true}
          />
        </Grid>
      </Grid>
    </StyledFilterCard>
  );
};

export default FilterCard;
